/**
 * Utility for working with Vercel Blob Storage
 * Loads prices from blob and applies them to materials arrays
 * 
 * Supports multiple price categories: A (default), B, C
 * Category is determined by URL parameter 'category'
 */

/**
 * Get price category from URL parameter
 * @returns {string} 'A', 'B', or 'C' (default: 'A')
 */
function getPriceCategory() {
  const params = new URLSearchParams(window.location.search);
  const category = params.get('category')?.toUpperCase();
  
  // Only B or C override default A
  if (category === 'B' || category === 'C') {
    return category;
  }
  
  return 'A';
}

/**
 * Export function to get price category for use in components
 */
export function getPriceCategoryForApp() {
  return getPriceCategory();
}

/**
 * Get prices filename based on category
 * @returns {string} Filename from environment variables
 */
function getPricesFilename() {
  const category = getPriceCategory();
  console.debug(`Prices category: ${category}`);
  
  switch (category) {
    case 'B':
      return process.env.REACT_APP_BLOB_PRICES_B_FILENAME;
    case 'C':
      return process.env.REACT_APP_BLOB_PRICES_C_FILENAME;
    case 'A':
    default:
      return process.env.REACT_APP_BLOB_PRICES_A_FILENAME;
  }
}

let loadedPrices = null;

async function fetchPrices() {
  const BLOB_BASE_URL = process.env.REACT_APP_BLOB_BASE_URL;
  const PRICES_FILENAME = getPricesFilename();
  
  // If env vars not set, work without prices
  if (!BLOB_BASE_URL || !PRICES_FILENAME) {
    console.warn('Prices not configured. App will work without prices.');
    loadedPrices = {};
    return loadedPrices;
  }

  const PRICES_URL = `${BLOB_BASE_URL}/${PRICES_FILENAME}`;
  
  try {
    console.debug(`Loading prices from: ${PRICES_FILENAME}`);
    
    const response = await fetch(PRICES_URL);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    loadedPrices = await response.json();
    
    // Check if empty
    if (!loadedPrices || Object.keys(loadedPrices).length === 0) {
      console.warn('Prices file is empty. App will work without prices.');
      loadedPrices = {};
      return loadedPrices;
    }
    
    console.debug(`Loaded ${Object.keys(loadedPrices).length} prices`);
    return loadedPrices;
  } catch (error) {
    console.warn(`Failed to load prices: ${error.message}. App will work without prices.`);
    loadedPrices = {};
    return loadedPrices;
  }
}

function applyPrices(items, prices) {
  return items.map(item => ({
    ...item,
    price: prices[item.code] !== undefined ? prices[item.code] : null
  }));
}

/**
 * Loads prices from blob (call once on app start)
 */
export async function waitForPrices() {
  loadedPrices = await fetchPrices();
}

/**
 * Applies loaded prices to materials array
 * Returns empty prices if not loaded yet
 */
export function fetchAndApplyPrices() {
  const prices = loadedPrices || {};
  
  return {
    applyTo: (materials) => applyPrices(materials, prices)
  };
}
